import { FunctionComponent } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import { IAppPalette, RECEDED_OPACITY } from './appPalette';
import { SkeletonLine } from './PaperFrame';
import { schoolInitials } from './schoolInitials';

/** Which decision this drawing is currently serving. Everything else recedes behind it. */
export type TDirectoryEmphasis = 'mark' | 'listing';

interface IDirectoryPreviewProps {
  palette: IAppPalette;
  schoolName: string;
  /** The resolved square mark: the square logo, else the school logo, else the letterhead one. */
  imageUrl?: string;
  /** False draws the list the way a dancer would actually see it — without this school in it. */
  isListed: boolean;
  emphasis: TDirectoryEmphasis;
}

/*
  True to the app, and deliberately so. The row is 48px tall with a 48px mark at an 8px radius; drawn
  at a phone-frame's scale that is 28px at 5px. The mark is NOT enlarged for emphasis even where it is
  the subject - the whole question an owner is asking here is "does my logo still read at the size the
  app actually draws it", and a flattering preview answers the wrong one.
*/
const MARK = 28;
const MARK_RADIUS = '5px';

/**
 * The app's "Find your dance school" list, as a dancer browsing for a studio sees it.
 *
 * One drawing serves both settings on this tab, the way `EmailFrame` serves sending and signature:
 * the square logo decides what the school's row LOOKS like, and the directory listing decides
 * whether the row is THERE at all. Drawing them together is what makes the pair legible — a mark
 * being perfected for a list the school has hidden itself from is worth seeing before you spend an
 * afternoon on it.
 */
export const DirectoryPreview: FunctionComponent<IDirectoryPreviewProps> = (props: IDirectoryPreviewProps) => {

  const { palette, schoolName, imageUrl, isListed, emphasis } = props;

  /*
    A neighbouring studio: no identity of its own, because it is context rather than content. It
    recedes only while this school's own row is on screen to be the subject; with the row gone, the
    neighbours ARE the drawing.
  */
  const renderNeighbour = (key: number, nameWidth: string) => (
    <Stack
      key={key}
      direction='row'
      alignItems='center'
      columnGap={1}
      sx={{
        backgroundColor: palette.surface,
        border: `1px solid ${palette.hairline}`,
        borderRadius: 1.5,
        px: 1,
        py: 1,
        opacity: isListed ? RECEDED_OPACITY : 1,
        transition: 'opacity 0.25s ease'
      }}
    >
      <Box sx={{ width: MARK, height: MARK, borderRadius: MARK_RADIUS, flexShrink: 0, backgroundColor: palette.hairline }} />
      <Stack rowGap={0.5} sx={{ flex: 1, minWidth: 0 }}>
        <SkeletonLine width={nameWidth} height={5} colour={`${palette.ink}8C`} />
        <SkeletonLine width='46%' colour={`${palette.medium}99`} />
      </Stack>
    </Stack>
  );

  const renderMark = () => {
    if (!imageUrl) {
      // The seal the app falls back to. Square, because this is the square slot - a circle here
      // would promise a shape the row does not draw.
      return (
        <Box sx={{
          width: MARK, height: MARK, borderRadius: MARK_RADIUS, flexShrink: 0,
          backgroundColor: `${palette.accent}1F`, color: palette.accent,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 700
        }}>
          {schoolInitials(schoolName)}
        </Box>
      );
    }

    /*
      `cover`, matching the app exactly. This is the honest part of the preview: a wide letterhead
      wordmark loses its ends here, and seeing that happen is the reason the square slot exists.
    */
    return (
      <Box
        component='img'
        src={imageUrl}
        alt=''
        sx={{ width: MARK, height: MARK, borderRadius: MARK_RADIUS, flexShrink: 0, objectFit: 'cover', objectPosition: 'center' }}
      />
    );
  };

  const renderOwnRow = () => (
    <Stack
      direction='row'
      alignItems='center'
      columnGap={1}
      sx={{
        backgroundColor: palette.surface,
        border: `1px solid ${emphasis === 'mark' ? `${palette.accent}66` : palette.hairline}`,
        borderRadius: 1.5,
        px: 1,
        py: 1,
        transition: 'border-color 0.25s ease'
      }}
    >
      {renderMark()}
      <Stack rowGap={0.5} sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ color: palette.ink, fontSize: 11, fontWeight: 600, minWidth: 0 }}>{schoolName}</Typography>
        <SkeletonLine width='52%' colour={`${palette.medium}B3`} />
      </Stack>
    </Stack>
  );

  return (
    <>
      <Typography sx={{ color: palette.ink, fontSize: 11, fontWeight: 700 }}>Find your dance school</Typography>

      <Stack rowGap={0.75}>
        {isListed
          ? (
            <>
              {renderOwnRow()}
              {renderNeighbour(1, '58%')}
              {renderNeighbour(2, '44%')}
            </>
          )
          : (
            /*
              Hidden. The school's row is simply gone and the others close up, which is the whole
              consequence - there is nothing to draw in its place, and inventing a marker for the
              absence would be drawing something no dancer will ever see.
            */
            <>
              {renderNeighbour(1, '58%')}
              {renderNeighbour(2, '44%')}
              {renderNeighbour(3, '62%')}
            </>
          )}
      </Stack>
    </>
  );
};
